/* Twin Lab — W14.x Organizational Twin Time Machine.
 * Navigation temporelle dans les corpus GELÉS (anti-hindsight : état à T = mois ≤ T).
 * Rien d'inventé : ce qui n'est pas reconstructible est déclaré. */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Play, Pause, ChevronLeft, ChevronRight, FlaskConical } from "lucide-react";

const TABS = [["state", "État à T"], ["events", "Événements"], ["compare", "Historical vs Fresh"],
  ["ledger", "Value Ledger"], ["failures", "Échecs W13→W14"], ["bh", "Before Human"]];
const SYS = [["platform", "Platform", "#EFD127"], ["deal", "Deal", "#378ADD"],
  ["financial_markets", "Financial Markets", "#1D9E75"], ["governance", "Governance", "#E24B4A"]];

const J = ({ v }) => (
  <pre className="text-[10px] text-zinc-400 whitespace-pre-wrap break-all bg-zinc-950 border border-zinc-800 rounded p-2 max-h-64 overflow-y-auto">
    {typeof v === "string" ? v : JSON.stringify(v, null, 1)}
  </pre>
);

function Stat({ label, value }) {
  return (
    <div className="flex justify-between gap-2 text-[11px] border-b border-zinc-900 py-1">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-200 font-medium text-right">{String(value ?? "—")}</span>
    </div>
  );
}

export default function TwinLab() {
  const [twins, setTwins] = useState([]);
  const [twin, setTwin] = useState(null);
  const [month, setMonth] = useState(1);
  const [tab, setTab] = useState("state");
  const [state, setState] = useState(null);
  const [aux, setAux] = useState({});
  const [err, setErr] = useState(null);
  const [playing, setPlaying] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/workspace/twin-lab/twins").then((r) => {
      setTwins(r.data.twins || []);
      if (r.data.twins?.length) setTwin(r.data.twins[0]);
    }).catch(() => setErr("Impossible de charger les twins (droits admin requis)."));
  }, []);

  useEffect(() => {
    if (!twin) return;
    // stale-while-revalidate : l'état précédent reste affiché pendant le chargement
    api.get(`/workspace/twin-lab/${twin.key}/state?month=${month}`)
      .then((r) => { setState(r.data); setErr(null); })
      .catch(() => setErr("Erreur de chargement de l'état — réessayez."));
  }, [twin, month]);

  useEffect(() => {
    if (!twin) return;
    if (tab === "events") {
      api.get(`/workspace/twin-lab/${twin.key}/month/${month}/events`).then((r) => setAux((a) => ({ ...a, events: r.data }))).catch(() => setErr("Erreur de chargement des événements."));
      return;
    }
    if (tab === "state") return;
    const ep = { compare: "compare", ledger: "value-ledger", failures: "failures", bh: "before-human" }[tab];
    api.get(`/workspace/twin-lab/${twin.key}/${ep}`).then((r) => { setAux((a) => ({ ...a, [tab]: r.data })); setErr(null); }).catch(() => setErr("Erreur de chargement de l'onglet."));
  }, [twin, month, tab]);

  useEffect(() => {
    if (playing && twin) {
      timer.current = setInterval(() => setMonth((m) => (m >= twin.months ? (setPlaying(false), m) : m + 1)), 2200);
      return () => clearInterval(timer.current);
    }
  }, [playing, twin]);

  if (!twin) return <div className="p-10 text-zinc-500"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="twin-lab-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 flex items-center gap-2">
            <FlaskConical size={18} className="text-[#EFD127]" /> Twin Lab — Organizational Time Machine
          </h1>
          <p className="text-xs text-zinc-500">Corpus gelé sha256 {twin.gt_sha256} · anti-hindsight strict · rien d'inventé, limites déclarées</p>
        </div>
        <div className="flex gap-2">
          {twins.map((t) => (
            <button key={t.key} data-testid={`twin-select-${t.key}`} onClick={() => { setTwin(t); setMonth(1); }}
              className={`px-3 py-1.5 rounded-full text-xs border ${twin.key === t.key ? "border-[#EFD127] text-[#EFD127] bg-[#3a2f00]/40" : "border-zinc-800 text-zinc-300"}`}
              onClickCapture={() => setAux({})}>
              {t.name.split("—")[0]}
            </button>
          ))}
        </div>
      </div>

      <div className="text-[11px] text-zinc-400 border border-zinc-800 rounded-lg p-2 bg-[#101013]">
        Ce laboratoire rejoue l'histoire d'une organisation telle que NOAM la connaissait à chaque mois (aucune information du futur).
        <span className="text-zinc-500"> W13/W14 = les deux campagnes de replay (avant/après corrections de frontières) · « mesure W14 » = agrégat mensuel enregistré pendant le replay · « Détections sémantiques » = signaux issus du contenu, avec preuve verbatim · HvF = clone avec mémoire vs clone sans mémoire.</span>
      </div>
      {err && <div className="text-xs text-red-400 border border-red-900 bg-red-950/30 rounded-lg p-2" data-testid="twin-error">{err}</div>}

      <div className="rounded-xl border border-zinc-800 bg-[#101013] p-4" data-testid="twin-timeline">
        <div className="flex items-center gap-3">
          <button data-testid="twin-prev" onClick={() => setMonth((m) => Math.max(1, m - 1))} className="p-1.5 rounded border border-zinc-700 text-zinc-300"><ChevronLeft size={14} /></button>
          <button data-testid="twin-play" onClick={() => setPlaying((p) => !p)} className="p-1.5 rounded border border-zinc-700 text-[#EFD127]">{playing ? <Pause size={14} /> : <Play size={14} />}</button>
          <button data-testid="twin-next" onClick={() => setMonth((m) => Math.min(twin.months, m + 1))} className="p-1.5 rounded border border-zinc-700 text-zinc-300"><ChevronRight size={14} /></button>
          <input data-testid="twin-slider" type="range" min={1} max={twin.months} value={month}
            onChange={(e) => setMonth(Number(e.target.value))} aria-label="Mois de la timeline" className="flex-1 accent-[#EFD127]" />
          <div className="text-sm text-[#EFD127] font-semibold w-32 text-right">{state?.label || `M${month}`} <span className="text-zinc-500 text-xs">M{month}/{twin.months}</span></div>
        </div>
        <div className="flex gap-[2px] mt-1.5 ml-[104px] mr-32">
          {(twin.activity || []).map((n, i) => (
            <div key={i} title={`M${i + 1} · ${n} événement(s)`} onClick={() => setMonth(i + 1)}
              className="flex-1 rounded-sm cursor-pointer"
              style={{ height: 6, background: i + 1 === month ? "#EFD127" : n === 0 ? "#1c1c21" : n > 12 ? "#378ADD" : "#1e3a5c" }} />
          ))}
        </div>
        <div className="text-[10px] text-zinc-500 mt-1">{twin.period} · densité d'événements par mois (cliquable)</div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`twin-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-lg text-xs border ${tab === k ? "border-sky-500 text-sky-300 bg-sky-950/30" : "border-zinc-800 text-zinc-400"}`}>{l}</button>
        ))}
      </div>

      {tab === "state" && (!state ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {SYS.map(([k, label, color]) => {
              const s = state[k] || {};
              return (
                <div key={k} className="rounded-xl border border-zinc-800 bg-[#101013] p-3" data-testid={`twin-system-${k}`}>
                  <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color }}>{label}</div>
                  {k === "platform" && <><Stat label="Objets connus" value={s.objets_connus} /><Stat label="Documents" value={s.documents_total} /><Stat label="Contacts" value={s.contacts_total} /><Stat label="Signaux (mesure W14)" value={s.signaux_mesures_w14} /><Stat label="Obligations (mesure W14)" value={s.obligations_mesures_w14} /><Stat label="Détections sémantiques Σ" value={s.detections_semantiques_cumulees} /><Stat label="Mémoire décisionnelle" value={s.memoire_decisionnelle} /></>}
                  {k === "deal" && <><Stat label="Deals actifs" value={s.deals_actifs} /><Stat label="Deals connus" value={s.deals_total} /><Stat label={`Valeur exposée ${twin.currency || "CHF"}`} value={(s.valeur_exposee_chf || 0).toLocaleString("fr-CH")} /><Stat label="Décisions" value={s.decisions_total} /></>}
                  {k === "financial_markets" && <><Stat label="Décisions FM" value={s.decisions_fm_total} /><Stat label="Révisions conviction" value={(s.revisions_conviction || []).length} /><Stat label="Outcomes" value={s.outcomes_total} /><Stat label="Positions" value="INSUFFICIENT_EVIDENCE" /><Stat label="Calibration" value="INSUFFICIENT_DATA" /></>}
                  {k === "governance" && <><Stat label="Politiques" value={(s.politiques || []).length} /><Stat label="Incidents" value={s.incidents_total} /><Stat label="Obligations ouvertes" value={(s.obligations_ouvertes || []).length} /><Stat label="Campagnes" value={(s.campagnes || []).length} /><Stat label="Comités" value={s.comites_total} /></>}
                </div>
              );
            })}
          </div>
          <div className="rounded-xl border border-zinc-800 bg-[#101013] p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Deals connus à {state.label} (phase telle que connue à T)</div>
            <div className="flex flex-wrap gap-1.5">
              {(state.deal?.deals || []).map((d, i) => (
                <span key={i} title={`${d.known_at} · ${d.source}`} className="text-[10px] px-2 py-0.5 rounded-full border border-zinc-800 text-zinc-300 bg-zinc-950">
                  {d.title} · <span className="text-sky-400">{d.phase}</span>
                </span>
              ))}
            </div>
          </div>
          <div className="text-[10px] text-amber-500/80 border border-amber-950 bg-amber-950/20 rounded-lg p-2">
            Limites de reconstruction (déclarées) : {(state.reconstruction_limits || []).join(" · ")}
          </div>
        </div>
      ))}

      {tab === "events" && (
        <div className="space-y-2" data-testid="twin-events">
          {(aux.events?.cards || []).length === 0 && <div className="text-xs text-zinc-500">Aucun événement ce mois dans le corpus gelé.</div>}
          {(aux.events?.cards || []).map((c, i) => (
            <details key={i} className="rounded-xl border border-zinc-800 bg-[#101013] p-3">
              <summary className="text-xs text-zinc-200 cursor-pointer">
                <span className="text-[#EFD127]">{c.type}</span> {c.detectable && <span className="text-[9px] text-emerald-500">détectable</span>} — {JSON.stringify(c.what_happened).slice(0, 110)}
              </summary>
              <div className="grid md:grid-cols-2 gap-2 mt-2">
                {[["WHAT HAPPENED", c.what_happened], ["WHAT NOAM KNEW", c.what_noam_knew], ["WHAT NOAM DETECTED", c.what_noam_detected],
                  ["WHAT NOAM RECOMMENDED", c.what_noam_recommended], ["WHAT THE ORGANIZATION DID", c.what_the_organization_did],
                  ["WHAT HAPPENED AFTER", c.what_happened_after]].map(([l, v]) => (
                  <div key={l}><div className="text-[9px] uppercase tracking-wider text-zinc-500">{l}</div><J v={v} /></div>
                ))}
              </div>
              <div className="text-[9px] text-zinc-600 mt-1">Provenance : {c.provenance?.source} · {c.provenance?.sha256} · connu à {c.provenance?.known_at}</div>
            </details>
          ))}
        </div>
      )}

      {tab === "compare" && aux.compare && (
        <div className="rounded-xl border border-zinc-800 bg-[#101013] p-4 space-y-2" data-testid="twin-compare">
          <div className="text-xs text-zinc-300 font-medium">{aux.compare.verdict || aux.compare.why}</div>
          {aux.compare.results && <div className="grid md:grid-cols-2 gap-2">
            <div><div className="text-[10px] text-emerald-400 uppercase">Historical (24 mois de mémoire)</div><J v={aux.compare.results.historical} /></div>
            <div><div className="text-[10px] text-red-400 uppercase">Fresh (même état, zéro mémoire)</div><J v={aux.compare.results.fresh} /></div>
          </div>}
        </div>
      )}

      {tab === "ledger" && aux.ledger && (
        <div className="space-y-2" data-testid="twin-ledger">
          {aux.ledger.entries.map((e, i) => (
            <div key={i} className="rounded-xl border border-zinc-800 bg-[#101013] p-3 flex justify-between gap-3">
              <div className="text-xs text-zinc-300">{e.intervention}<div className="text-[10px] text-zinc-600 mt-0.5">{e.why}</div></div>
              <div className="text-right shrink-0"><div className="text-[10px] text-sky-400">{e.category}</div><div className="text-xs text-amber-400 font-semibold">{e.value}</div></div>
            </div>
          ))}
          <div className="text-[11px] text-zinc-400 border border-zinc-800 rounded-lg p-2">{aux.ledger.honest_statement}</div>
        </div>
      )}

      {tab === "failures" && aux.failures && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="twin-failures">
          {["w13", "w14"].map((w) => (
            <div key={w} className="rounded-xl border border-zinc-800 bg-[#101013] p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{w.toUpperCase()} — {aux.failures[w]?.fails_total} échecs</div>
              {Object.entries(aux.failures[w]?.taxonomy || {}).map(([k, v]) => <Stat key={k} label={k} value={v} />)}
              <div className={`mt-2 text-xs font-semibold ${w === "w14" ? "text-emerald-400" : "text-red-400"}`}>
                UNJUSTIFIED FAILURE RATE : {aux.failures[w]?.unjustified_failure_rate_pct}%
                <span className="text-zinc-500 font-normal"> ({aux.failures[w]?.unjustified_failures}/{aux.failures[w]?.denominator_events} événements du corpus)</span>
              </div>
              <div className="text-[9px] text-zinc-600 mt-1">{aux.failures[w]?.note}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "bh" && aux.bh && (
        <div className="rounded-xl border border-zinc-800 bg-[#101013] p-4 space-y-2" data-testid="twin-before-human">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-2xl font-bold text-zinc-100">{aux.bh.events_detectables}</div><div className="text-[10px] text-zinc-500">événements détectables (corpus)</div></div>
            <div><div className="text-2xl font-bold text-amber-400">{aux.bh.detected_before_human}</div><div className="text-[10px] text-zinc-500">signal antérieur au mois humain (corrélation)</div></div>
            <div><div className="text-2xl font-bold text-red-400">{aux.bh.confirmed_object_level}</div><div className="text-[10px] text-zinc-500">confirmés objet-à-objet</div></div>
          </div>
          <div className="text-xs text-zinc-300">{aux.bh.verdict}</div>
          <div className="text-[10px] text-zinc-500">{aux.bh.method}</div>
        </div>
      )}
    </div>
  );
}
